// TrashSection — admin Papierkorb UI. Mirrors AuditLogSection's table styling
// and AdminDepartments' inline confirm-delete pattern (Admin.tsx).

import { useState } from "preact/hooks";
import {
  Building2,
  FolderKanban,
  CheckSquare,
  ListTodo,
  ClipboardList,
  RotateCcw,
  Trash2,
} from "lucide-preact";
import {
  useAdminTrash,
  restoreTrashItem,
  purgeTrashItem,
  type TrashEntityType,
  type TrashItem,
} from "../../store/adminTrash";
import { toastError, toastSuccess } from "../../lib/toast";

import { Button } from "../ui/Button";

const ENTITY_ICON: Record<TrashEntityType, typeof Building2> = {
  space: Building2,
  project: FolderKanban,
  task: CheckSquare,
  taskList: ListTodo,
  personalTask: ClipboardList,
};

const ENTITY_LABEL: Record<TrashEntityType, string> = {
  space: "Space",
  project: "Projekt",
  task: "Aufgabe",
  taskList: "Liste",
  personalTask: "Persönliche Aufgabe",
};

export function TrashSection(): preact.JSX.Element {
  const { items, loading, error, retentionDays } = useAdminTrash();
  const [confirmPurgeKey, setConfirmPurgeKey] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const keyOf = (item: TrashItem): string => `${item.entity_type}:${item.id}`;

  const handleRestore = async (item: TrashItem): Promise<void> => {
    const key = keyOf(item);
    setBusyKey(key);
    try {
      await restoreTrashItem(item.entity_type, item.id);
      toastSuccess(`„${item.name}" wiederhergestellt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Wiederherstellen fehlgeschlagen");
    } finally {
      setBusyKey(null);
    }
  };

  const handlePurge = async (item: TrashItem): Promise<void> => {
    const key = keyOf(item);
    setBusyKey(key);
    try {
      await purgeTrashItem(item.entity_type, item.id);
      setConfirmPurgeKey(null);
      toastSuccess(`„${item.name}" endgültig gelöscht.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section class="space-y-4">
      <p class="text-sm text-[color:var(--color-on-surface-variant)]">
        Gelöschte Spaces, Projekte, Aufgaben, Listen und persönliche Aufgaben landen
        hier. Nach {retentionDays} Tagen werden sie automatisch endgültig entfernt.
      </p>

      <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-x-auto">
        {loading ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)]">
            Lädt…
          </p>
        ) : error ? (
          <p class="p-4 text-sm text-[color:var(--color-error)]">{error}</p>
        ) : items.length === 0 ? (
          <p class="p-4 text-sm text-[color:var(--color-on-surface-variant)] italic">
            Der Papierkorb ist leer.
          </p>
        ) : (
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-[color:var(--color-outline-variant)] text-left text-xs text-[color:var(--color-on-surface-variant)]">
                <th class="px-4 py-2 font-medium">Objekt</th>
                <th class="px-4 py-2 font-medium">Kontext</th>
                <th class="px-4 py-2 font-medium">Gelöscht von</th>
                <th class="px-4 py-2 font-medium">Gelöscht am</th>
                <th class="px-4 py-2 font-medium">Verbleibend</th>
                <th class="px-4 py-2 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-[color:var(--color-outline-variant)]">
              {items.map((item) => {
                const Icon = ENTITY_ICON[item.entity_type];
                const key = keyOf(item);
                const busy = busyKey === key;
                return (
                  <tr
                    key={key}
                    class="hover:bg-[color:var(--color-surface-container-low)] transition-colors"
                  >
                    <td class="px-4 py-2.5">
                      <div class="flex items-center gap-2">
                        <Icon
                          size={14}
                          class="shrink-0 text-[color:var(--color-on-surface-variant)]"
                          aria-hidden="true"
                        />
                        <div>
                          <div class="text-[color:var(--color-on-surface)]">{item.name}</div>
                          <div class="text-xs text-[color:var(--color-on-surface-variant)]">
                            {ENTITY_LABEL[item.entity_type]}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface-variant)]">
                      {item.context ?? "—"}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface)]">
                      {item.deleted_by_name ?? (
                        <span class="italic text-[color:var(--color-on-surface-variant)]">
                          Unbekannt
                        </span>
                      )}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface-variant)]">
                      {new Date(item.deleted_at).toLocaleString("de-DE")}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap text-[color:var(--color-on-surface-variant)]">
                      {item.days_until_purge === 0
                        ? "heute"
                        : `${item.days_until_purge} Tag${item.days_until_purge === 1 ? "" : "e"}`}
                    </td>
                    <td class="px-4 py-2.5 whitespace-nowrap">
                      <div class="flex items-center gap-1.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleRestore(item)}
                          class="inline-flex items-center gap-1 rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-40 transition-colors"
                        >
                          <RotateCcw size={14} aria-hidden="true" />
                          Wiederherstellen
                        </button>
                        {confirmPurgeKey === key ? (
                          <>
                            <Button
                              variant="destructive"
                              size="xs"
                              type="button"
                              disabled={busy}
                              onClick={() => void handlePurge(item)}
                            >
                              Wirklich endgültig löschen
                            </Button>
                            <button
                              type="button"
                              onClick={() => setConfirmPurgeKey(null)}
                              class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
                            >
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmPurgeKey(key)}
                            class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors"
                            aria-label={`${item.name} endgültig löschen`}
                          >
                            <Trash2 size={14} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
